import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';
import {
  type ObservabilityContext,
  withTraceContext,
} from './observability';

/**
 * Structured frontend logger with trace correlation.
 * Console: only error/fatal by default. To mirror trace/debug/info/warn locally, set
 * ATTIK_OBSERVABILITY_VERBOSE_CONSOLE=1 (server) and/or NEXT_PUBLIC_ATTIK_OBSERVABILITY_VERBOSE_CONSOLE=1 (browser).
 */
const verboseObservabilityConsole =
  process.env.ATTIK_OBSERVABILITY_VERBOSE_CONSOLE === '1' ||
  process.env.ATTIK_OBSERVABILITY_VERBOSE_CONSOLE === 'true' ||
  process.env.NEXT_PUBLIC_ATTIK_OBSERVABILITY_VERBOSE_CONSOLE === '1' ||
  process.env.NEXT_PUBLIC_ATTIK_OBSERVABILITY_VERBOSE_CONSOLE === 'true';

function mirrorLevel(
  level: 'trace' | 'debug' | 'info' | 'warn',
  message: string,
  ctx: ObservabilityContext
): void {
  if (process.env.NODE_ENV === 'production' || !verboseObservabilityConsole) return;
  const line = `[${level.toUpperCase()}] ${message}`;
  if (level === 'trace' || level === 'debug') console.debug(line, ctx || '');
  else if (level === 'info') console.info(line, ctx || '');
  else console.warn(line, ctx || '');
}

function captureLog(
  level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal',
  message: string,
  ctx: ObservabilityContext
): void {
  posthog.capture(EVENTS.frontendLog, {
    level,
    message,
    ...ctx,
  });
}

export const frontendLogger = {
  trace: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('trace', message, ctx);
    mirrorLevel('trace', message, ctx);
  },

  debug: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('debug', message, ctx);
    mirrorLevel('debug', message, ctx);
  },

  info: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('info', message, ctx);
    mirrorLevel('info', message, ctx);
  },

  warn: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('warn', message, ctx);
    mirrorLevel('warn', message, ctx);
  },

  error: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('error', message, ctx);
    console.error(`[ERROR] ${message}`, ctx || '');
  },

  fatal: (message: string, context?: ObservabilityContext) => {
    const ctx = withTraceContext(context);
    captureLog('fatal', message, ctx);
    console.error(`[FATAL] ${message}`, ctx || '');
  },
};

export function createLifecycleLogger(
  category: string,
  title: string,
  contextBuilder?: (context?: ObservabilityContext) => ObservabilityContext
) {
  const withCategory = (context?: ObservabilityContext) => ({
    category,
    surface: 'web',
    ...(contextBuilder ? contextBuilder(context) : withTraceContext(context)),
  });

  return {
    started: (context?: ObservabilityContext) =>
      frontendLogger.info(`${title} started`, withCategory({ ...context, result: 'started' })),
    completed: (context?: ObservabilityContext) =>
      frontendLogger.info(`${title} completed`, withCategory({ ...context, result: 'success' })),
    failed: (error: Error | string, context?: ObservabilityContext) =>
      frontendLogger.error(`${title} failed`, withCategory({
        ...context,
        error: error instanceof Error ? error.message : error,
        result: 'error',
      })),
    skipped: (reason: string, context?: ObservabilityContext) =>
      frontendLogger.info(`${title} skipped`, withCategory({
        ...context,
        reason,
        result: 'skipped',
      })),
    retried: (context?: ObservabilityContext) =>
      frontendLogger.warn(`${title} retried`, withCategory({ ...context, result: 'retry' })),
  };
}
